/**
 * DI-3-5 — the list row's flight chips: flights · enabled · offset + route ·
 * images (+ "another operator's log" when a flight is foreign). D91 chips via
 * the shared `statusChipStyle` (the same recipe as `DataAcquisitionStatusChip`
 * — hue lifted to AA as the label, no keyline). No blob-state chip here: the
 * counts are a detail read (orchestrator decision 2).
 */
import type { ReactElement } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { statusChipStyle, type StatusSlot } from "@aeriosiq/ui";
import { flightFacts, listChips, type FlightBearing } from "./flightFacts.js";

/** One D91 chip; `data-chip` / `data-slot` are the test hooks. */
export function FlightChip({
  label,
  slot,
  tooltip,
  testId,
}: {
  label: string;
  slot: StatusSlot;
  tooltip?: string | undefined;
  testId?: string | undefined;
}): ReactElement {
  const chip = (
    <Chip
      size="small"
      label={label}
      data-chip=""
      data-slot={slot}
      {...(testId ? { "data-testid": testId } : {})}
      sx={(t) => {
        const s = statusChipStyle(t, slot);
        return { backgroundColor: s.backgroundColor, color: s.color };
      }}
    />
  );
  return tooltip ? (
    <Tooltip
      title={<span style={{ whiteSpace: "pre-line" }}>{tooltip}</span>}
      arrow
    >
      {chip}
    </Tooltip>
  ) : (
    chip
  );
}

export function FlightChips({ row }: { row: FlightBearing }): ReactElement {
  const chips = listChips(flightFacts(row));
  return (
    <Box
      data-testid="flight-chips"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        height: "100%",
        overflow: "hidden",
      }}
    >
      {chips.length === 0 ? (
        <Typography variant="body2" component="span" color="text.secondary">
          —
        </Typography>
      ) : (
        chips.map((c) => (
          <FlightChip
            key={c.key}
            label={c.label}
            slot={c.slot}
            tooltip={c.tooltip}
          />
        ))
      )}
    </Box>
  );
}
